import React, { useEffect, useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';

import { useGetHrxModuleListQuery } from '../../../redux/apis/HrxModuleApi';
import {
  useGetSystemRoleByIdQuery,
  useUpdateSystemRoleMutation
} from '../../../redux/apis/systemRoleApi';
import { Skeleton } from '../../../components/UI/Skeleton/Skeleton';
import EmptyData from '../../../components/UI/EmptyData';

import './system-role-details.css';

const SystemRoleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [selectedModules, setSelectedModules] = useState([]);

  const {
    data: roleDetails,
    isLoading: isRoleLoading,
    refetch: refetchRoleDetails
  } = useGetSystemRoleByIdQuery(id, { skip: !id });

  const {
    data: moduleList,
    isLoading: isModuleLoading,
    isFetching,
    refetch: refetchModules
  } = useGetHrxModuleListQuery({ page: 1, limit: 50 });

  const [updateRole] = useUpdateSystemRoleMutation();

  useEffect(() => {
    refetchRoleDetails();
    refetchModules();
  }, [id]);

  useEffect(() => {
    if (roleDetails?.data?.accessModule?.length) {
      const cleanModules = roleDetails.data.accessModule
        .map((item) => item?.moduleId?._id)
        .filter(Boolean);

      setSelectedModules(cleanModules);
    }
  }, [roleDetails]);

  const handleToggle = async (moduleId) => {
    if (!moduleId) return;

    const prevModules = [...selectedModules];

    const updatedModules = prevModules.includes(moduleId)
      ? prevModules.filter((id) => id !== moduleId)
      : [...prevModules, moduleId];

    const cleanModules = updatedModules.filter(Boolean);
    setSelectedModules(cleanModules);

    try {
      await updateRole({
        id,
        body: {
          accessModules: cleanModules.map((mId) => ({
            moduleId: mId
          }))
        }
      }).unwrap();
    } catch (error) {
      console.error('Update failed', error);
      setSelectedModules(prevModules);
    }
  };

  const groupedModules = useMemo(() => {
    const list = moduleList?.data || [];
    const parentMap = {};
    const result = [];

    list.forEach((mod) => {
      if (!mod.parent) {
        parentMap[mod._id] = { ...mod, children: [] };
        result.push(parentMap[mod._id]);
      }
    });

    list.forEach((mod) => {
      if (mod.parent?._id && parentMap[mod.parent._id]) {
        parentMap[mod.parent._id].children.push(mod);
      }
    });

    return result;
  }, [moduleList]);

  const enabledCount = selectedModules.length;
  const isLoadingState = isRoleLoading || isModuleLoading || isFetching;

  return (
    <div className="role-details container-xxl">
      <div className="role-details__card">
        {/* Header */}
        <div className="role-details__header">
          <button type="button" className="role-details__back" onClick={() => navigate(-1)}>
            <i className="icofont-rounded-left" aria-hidden /> Back
          </button>

          <div className="role-details__header-main">
            <span className="role-details__role-icon" aria-hidden>
              <i className="icofont-users-alt-5" />
            </span>
            <div className="role-details__header-text">
              <span className="role-details__eyebrow">System role</span>
              <h2 className="role-details__role-name">{roleDetails?.data?.roleName || '-'}</h2>
            </div>
          </div>

          <span className="role-details__count" aria-label="Enabled modules">
            <i className="icofont-check-circled" aria-hidden />
            {enabledCount} enabled
          </span>
        </div>

        {/* Subhead */}
        <div className="role-details__subhead">
          <span className="role-details__subhead-title">Module access</span>
          <span className="role-details__subhead-hint">
            Toggle the modules this role can access. Changes save automatically.
          </span>
        </div>

        {/* Body */}
        <div className="role-details__body">
          {isLoadingState ? (
            <div className="role-details__skeleton" role="status" aria-busy="true">
              <span className="visually-hidden">Loading modules…</span>
              {Array.from({ length: 6 }).map((_, i) => (
                <div className="role-details__skeleton-row" key={i}>
                  <Skeleton variant="text" width={`${40 + (i % 3) * 12}%`} />
                  <Skeleton width={40} height={22} style={{ borderRadius: 999 }} />
                </div>
              ))}
            </div>
          ) : groupedModules.length === 0 ? (
            <EmptyData title="No modules found" />
          ) : (
            <div className="role-details__list">
              {groupedModules.map((row) => {
                const hasChildren = row.children?.length > 0;
                const enabledChildren = hasChildren
                  ? row.children.filter((c) => selectedModules.includes(c._id)).length
                  : 0;
                return (
                  <div
                    className={`role-module ${hasChildren ? 'role-module--group' : ''}`}
                    key={row._id}
                  >
                    <div className="role-parent">
                      <span className="role-parent-left">
                        <span className="role-parent-icon" aria-hidden>
                          <i className={hasChildren ? 'icofont-folder' : 'icofont-cube'} />
                        </span>
                        <span className="role-parent-text">
                          <span className="role-parent-title">{row.name}</span>
                          {hasChildren && (
                            <span className="role-parent-meta">
                              {enabledChildren}/{row.children.length} sub-modules enabled
                            </span>
                          )}
                        </span>
                      </span>
                      <Form.Check
                        type="switch"
                        className="role-switch"
                        checked={selectedModules.includes(row._id)}
                        onChange={() => handleToggle(row._id)}
                      />
                    </div>

                    {hasChildren && (
                      <div className="role-children">
                        {row.children.map((child) => (
                          <div key={child._id} className="role-child">
                            <span className="role-child-title">
                              <span className="role-child-bullet" aria-hidden />
                              {child.name}
                            </span>
                            <Form.Check
                              type="switch"
                              className="role-switch"
                              checked={selectedModules.includes(child._id)}
                              onChange={() => handleToggle(child._id)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemRoleDetails;
